import React, { useState, useEffect, useCallback } from 'react';
import {
  Save, Shield, UserPlus, User as UserIcon, CheckCircle2, Ban, Copy,
  Palette, Globe, Bell, Info, Sparkles, RefreshCw, Trash2, KeyRound,
  Plus, AlertTriangle, Fingerprint, Loader2,
} from 'lucide-react';
import axios from 'axios';
import { preferencesApi, authApi, apiKeysApi, passkeysApi, calendarSourcesApi } from '../api/services';
import type { PasskeyCredentialInfo } from '../api/services';
import { startRegistration } from '@simplewebauthn/browser';
import { useAuth } from '../auth/AuthProvider';
import { useThemePreferences } from '../theme/ThemeContext';
import { isDyslexiaTheme } from '../theme/theme';
import type { PlanningPreference, CalendarSource } from '../types/models';
import { useDashboardVariant } from '../hooks/useDashboardVariant';
import { useUnifiedPlanVariant } from '../hooks/useUnifiedPlanVariant';
import OutOfOfficeCard from '../components/ooo/OutOfOfficeCard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function SectionHeader({ icon: Icon, title, right }: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-5 w-5" />}
        <h3 className="text-base font-semibold">{title}</h3>
      </div>
      {right}
    </div>
  );
}

function MessageAlert({ message, onClose }: {
  message: { type: 'success' | 'error' | 'info' | 'warning'; text: string } | null;
  onClose: () => void;
}) {
  if (!message) return null;
  const variant = message.type === 'error' ? 'error' : message.type === 'success' ? 'success' : message.type === 'warning' ? 'warning' : 'default';
  return (
    <Alert variant={variant as any} className="mb-3">
      <div className="flex items-start justify-between gap-2">
        <AlertDescription>{message.text}</AlertDescription>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </Alert>
  );
}

function SwitchRow({ checked, onChange, label, description, disabled }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className={cn('flex items-start gap-3 cursor-pointer', disabled && 'opacity-60 cursor-not-allowed')}>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} className="mt-0.5" />
      <div className="min-w-0">
        <div className="text-sm">{label}</div>
        {description && <div className="text-xs text-muted-foreground">{description}</div>}
      </div>
    </label>
  );
}

function CheckboxRow({ checked, onChange, label, description, disabled }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className={cn('flex items-start gap-2 cursor-pointer', disabled && 'opacity-60 cursor-not-allowed')}>
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
        disabled={disabled}
        className="mt-0.5"
      />
      <div className="min-w-0">
        <div className="text-sm">{label}</div>
        {description && <div className="text-xs text-muted-foreground">{description}</div>}
      </div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// MFA Setup
// ---------------------------------------------------------------------------

function MfaSetupSection() {
  const { user } = useAuth();
  const [mfaEnabled, setMfaEnabled] = useState<boolean | null>(null);
  const [setupData, setSetupData] = useState<{ sharedKey: string; authenticatorUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    authApi.me().then(data => setMfaEnabled(data.mfaEnabled)).catch(() => {});
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authApi.setupMfa();
      setSetupData(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await authApi.enableMfa(code);
      setRecoveryCodes(data.recoveryCodes);
      setMfaEnabled(true);
      setSuccess('MFA enabled successfully');
      setSetupData(null);
      setCode('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyKey = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (mfaEnabled === null) return null;

  return (
    <Card>
      <CardContent className="pt-6">
        <SectionHeader
          icon={Shield}
          title="Multi-Factor Authentication"
          right={mfaEnabled ? (
            <Badge variant="success" className="gap-1">
              <CheckCircle2 className="h-3 w-3" /> Enabled
            </Badge>
          ) : null}
        />

        {error && <MessageAlert message={{ type: 'error', text: error }} onClose={() => setError(null)} />}
        {success && <MessageAlert message={{ type: 'success', text: success }} onClose={() => setSuccess(null)} />}

        {mfaEnabled && !recoveryCodes && (
          <p className="text-sm text-muted-foreground">
            MFA is enabled on your account. Your login requires a verification code from your authenticator app.
          </p>
        )}

        {!mfaEnabled && !setupData && (
          <div>
            <p className="mb-3 text-sm text-muted-foreground">
              Add an extra layer of security to your account by enabling MFA with an authenticator app (Microsoft Authenticator, Google Authenticator, etc.).
            </p>
            <Button variant="outline" onClick={handleSetup} disabled={loading} className="gap-1.5">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
              Set Up MFA
            </Button>
          </div>
        )}

        {setupData && (
          <div>
            <p className="mb-2 text-sm">Scan this key in your authenticator app, or enter it manually:</p>
            <div className="mb-3 flex items-center gap-2">
              <Input
                value={setupData.sharedKey}
                readOnly
                className="font-mono tracking-widest"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={() => copyKey(setupData.sharedKey)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy key</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
              App name: <strong>Cadence</strong> · Account: <strong>{user?.email}</strong>
            </p>
            <div className="my-3 border-t border-border" />
            <p className="mb-2 text-sm">Enter the 6-digit code from your authenticator app to verify:</p>
            <div className="flex items-start gap-2">
              <Input
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-44 text-center font-mono text-lg tracking-[0.5em]"
                maxLength={6}
              />
              <Button onClick={handleEnable} disabled={loading || code.length !== 6}>
                {loading ? 'Verifying...' : 'Enable MFA'}
              </Button>
            </div>
          </div>
        )}

        {recoveryCodes && (
          <div className="mt-3">
            <Alert variant="warning" className="mb-3">
              <AlertDescription>
                Save these recovery codes in a safe place. Each code can only be used once to sign in if you lose access to your authenticator app.
              </AlertDescription>
            </Alert>
            <div className="rounded-md bg-muted p-3 font-mono text-sm">
              {recoveryCodes.map((rc, i) => (
                <div key={i}>{rc}</div>
              ))}
            </div>
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => copyKey(recoveryCodes.join('\n'))}>
              Copy All Codes
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Passkeys
// ---------------------------------------------------------------------------

function PasskeysSection() {
  const [passkeys, setPasskeys] = useState<PasskeyCredentialInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addLoading, setAddLoading] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [deviceName, setDeviceName] = useState('');

  const loadPasskeys = useCallback(async () => {
    try {
      const data = await passkeysApi.list();
      setPasskeys(data);
    } catch {
      setError('Failed to load passkeys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPasskeys(); }, [loadPasskeys]);

  const handleAdd = async () => {
    setAddLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const options = await passkeysApi.registerOptions();
      const attestation = await startRegistration({ optionsJSON: options });
      await passkeysApi.register({ response: attestation, deviceName: deviceName || undefined });
      setSuccess('Passkey registered successfully.');
      setDeviceName('');
      setShowNameDialog(false);
      loadPasskeys();
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey registration was cancelled.');
      } else {
        setError(err.response?.data?.error || 'Failed to register passkey.');
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!window.confirm('Remove this passkey? You will no longer be able to sign in with it.')) return;
    try {
      await passkeysApi.remove(id);
      loadPasskeys();
    } catch {
      setError('Failed to remove passkey');
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return '--';
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <SectionHeader
          icon={Fingerprint}
          title="Passkeys"
          right={
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNameDialog(true)}
              disabled={addLoading}
              className="gap-1.5"
            >
              {addLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add Passkey
            </Button>
          }
        />

        {error && <MessageAlert message={{ type: 'error', text: error }} onClose={() => setError(null)} />}
        {success && <MessageAlert message={{ type: 'success', text: success }} onClose={() => setSuccess(null)} />}

        <p className="mb-3 text-sm text-muted-foreground">
          Passkeys let you sign in with your fingerprint, face, or security key instead of a password.
        </p>

        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : passkeys.length === 0 ? (
          <p className="py-1 text-sm text-muted-foreground">
            No passkeys registered yet. Add one to enable passwordless sign-in.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {passkeys.map(pk => (
              <div
                key={pk.id}
                className="flex items-center justify-between rounded-md bg-muted p-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Fingerprint className="h-4 w-4" />
                    <span className="text-sm font-semibold">{pk.deviceName || 'Passkey'}</span>
                    {pk.credentialType && (
                      <Badge variant="outline" className="text-[10px]">{pk.credentialType}</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Added {formatDate(pk.createdAt)}
                    {pk.lastUsedAt && <> · Last used {formatDate(pk.lastUsedAt)}</>}
                  </div>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleRemove(pk.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Remove passkey</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            ))}
          </div>
        )}

        <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Passkey</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="passkey-name">Device Name (optional)</Label>
              <Input
                id="passkey-name"
                placeholder='e.g. "MacBook Pro", "YubiKey 5"'
                value={deviceName}
                onChange={e => setDeviceName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">A friendly name to help you identify this passkey later</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowNameDialog(false)}>Cancel</Button>
              <Button onClick={handleAdd} disabled={addLoading} className="gap-1.5">
                {addLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />}
                {addLoading ? 'Registering...' : 'Continue'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Session Management
// ---------------------------------------------------------------------------

function SessionManagementSection() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [lastLogin, setLastLogin] = useState<string | null>(null);

  useEffect(() => {
    authApi.me().then((data: any) => {
      if (data.lastLoginAt) setLastLogin(data.lastLoginAt);
    }).catch(() => {});
  }, []);

  const handleRevokeSessions = async () => {
    if (!window.confirm('This will sign out all other devices and browser sessions. Continue?')) return;
    setLoading(true);
    setMessage(null);
    try {
      const result = await authApi.revokeSessions();
      const stored = sessionStorage.getItem('auth');
      if (stored && result.accessToken) {
        try {
          const auth = JSON.parse(stored);
          auth.accessToken = result.accessToken;
          auth.refreshToken = result.refreshToken;
          sessionStorage.setItem('auth', JSON.stringify(auth));
        } catch { /* ignore parse error */ }
      }
      setMessage({ type: 'success', text: result.message || 'All other sessions have been revoked.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to revoke sessions.' });
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const browserInfo = navigator.userAgent;
  const simpleBrowser = (() => {
    if (browserInfo.includes('Firefox')) return 'Firefox';
    if (browserInfo.includes('Edg')) return 'Edge';
    if (browserInfo.includes('Chrome')) return 'Chrome';
    if (browserInfo.includes('Safari')) return 'Safari';
    return 'Unknown browser';
  })();
  const simpleOs = (() => {
    if (browserInfo.includes('Mac')) return 'macOS';
    if (browserInfo.includes('Windows')) return 'Windows';
    if (browserInfo.includes('Linux')) return 'Linux';
    if (browserInfo.includes('iPhone') || browserInfo.includes('iPad')) return 'iOS';
    if (browserInfo.includes('Android')) return 'Android';
    return 'Unknown OS';
  })();

  return (
    <Card>
      <CardContent className="pt-6">
        <SectionHeader icon={Shield} title="Session Security" />
        <MessageAlert message={message} onClose={() => setMessage(null)} />

        <div className="mb-3 rounded-md bg-muted p-3">
          <p className="mb-1 text-sm font-semibold">Current Session</p>
          <p className="text-sm text-muted-foreground">{simpleBrowser} on {simpleOs}</p>
          {lastLogin && (
            <p className="text-sm text-muted-foreground">
              Last login: {new Date(lastLogin).toLocaleString()}
            </p>
          )}
        </div>

        <p className="mb-3 text-sm text-muted-foreground">
          If you suspect unauthorized access, you can sign out all other sessions. This will invalidate all existing refresh tokens on other devices.
        </p>

        <Button
          variant="outline"
          onClick={handleRevokeSessions}
          disabled={loading}
          className="gap-1.5 text-destructive border-destructive/40 hover:bg-destructive/10"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
          {loading ? 'Revoking...' : 'Sign Out All Other Sessions'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Account Info
// ---------------------------------------------------------------------------

function AccountInfoSection() {
  const { user } = useAuth();
  if (!user) return null;

  const roleLabel = (role: string) => {
    switch (role) {
      case 'ExecutiveAssistant': return 'Executive Assistant';
      case 'TenantAdmin': return 'Tenant Admin';
      case 'PlatformAdmin': return 'Platform Admin';
      default: return role;
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <SectionHeader icon={UserIcon} title="Account" />
        <div className="flex items-center gap-2 rounded-md bg-muted p-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{user.displayName}</p>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
          <Badge variant="outline" className="border-primary text-primary">{roleLabel(user.role)}</Badge>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// API Keys
// ---------------------------------------------------------------------------

function ApiKeysSection() {
  const [keys, setKeys] = useState<Array<{
    id: string; name: string; keyPrefix: string; createdAt: string;
    expiresAt: string | null; lastUsedAt: string | null; scopes: string | null;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState<string>('90');
  const [createLoading, setCreateLoading] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      const data = await apiKeysApi.list();
      setKeys(data);
    } catch {
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreateLoading(true);
    setError(null);
    try {
      const expiryDays = newKeyExpiry === 'never' ? undefined : parseInt(newKeyExpiry);
      const result = await apiKeysApi.create({ name: newKeyName.trim(), expiryDays });
      setCreatedKey(result.key);
      setShowCreate(false);
      setNewKeyName('');
      setNewKeyExpiry('90');
      loadKeys();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create API key');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await apiKeysApi.revoke(id);
      loadKeys();
    } catch {
      setError('Failed to revoke key');
    }
  };

  const handleCopyKey = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return '--';
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <SectionHeader
          icon={KeyRound}
          title="Your API Keys"
          right={
            <Button size="sm" onClick={() => { setShowCreate(true); setCreatedKey(null); }} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Generate New Key
            </Button>
          }
        />

        {error && <MessageAlert message={{ type: 'error', text: error }} onClose={() => setError(null)} />}

        {createdKey && (
          <Alert variant="warning" className="mb-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <p className="mb-1 text-sm font-semibold">Save this key now -- it will not be shown again.</p>
                <div className="flex items-center gap-2">
                  <Input
                    value={createdKey}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button variant="ghost" size="icon" onClick={handleCopyKey}>
                    {copySuccess ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : keys.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            No API keys yet. For Claude Desktop and other OAuth-capable MCP clients you don't need one — see AI Assistant Integration below. API keys are still useful for CLI scripts, CI jobs, or terminal MCP clients.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {keys.map(k => (
              <div key={k.id} className="flex items-center justify-between rounded-md bg-muted p-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{k.name}</span>
                    <Badge variant="secondary" className="font-mono text-[10px]">{k.keyPrefix}...</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Created {formatDate(k.createdAt)}
                    {k.lastUsedAt && <> · Last used {formatDate(k.lastUsedAt)}</>}
                    {k.expiresAt ? <> · Expires {formatDate(k.expiresAt)}</> : <> · No expiry</>}
                  </div>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleRevoke(k.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Revoke key</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            ))}
          </div>
        )}

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate New API Key</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="key-name">Key Name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g. MCP Server, Claude Desktop"
                  value={newKeyName}
                  onChange={e => setNewKeyName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">A friendly name to identify this key</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Expiry</Label>
                <Select value={newKeyExpiry} onValueChange={setNewKeyExpiry}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                    <SelectItem value="never">Never expires</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={createLoading || !newKeyName.trim()}>
                {createLoading ? 'Generating...' : 'Generate Key'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// User Management (Admin only)
// ---------------------------------------------------------------------------

function UserManagementSection() {
  const { user } = useAuth();
  const [users, setUsers] = useState<Array<{
    id: string; email: string; displayName: string; role: string;
    isActive: boolean; createdAt: string; lastLoginAt: string | null; mfaEnabled: boolean;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', displayName: '', role: 'ExecutiveAssistant' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const isAdmin = user?.role === 'PlatformAdmin' || user?.role === 'Admin';

  const loadUsers = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    try {
      const data = await authApi.getUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.status === 403 ? 'Insufficient permissions' : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleToggleActive = async (id: string) => {
    try {
      await authApi.toggleUserActive(id);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let pw = '';
    const arr = new Uint8Array(20);
    crypto.getRandomValues(arr);
    for (let i = 0; i < 20; i++) pw += chars[arr[i] % chars.length];
    return pw;
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.displayName || !newUser.password) {
      setAddError('All fields are required');
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await authApi.registerUser({
        email: newUser.email,
        password: newUser.password,
        displayName: newUser.displayName,
        tenantId: user?.tenantId || '',
        role: newUser.role,
      });
      setShowAdd(false);
      setNewUser({ email: '', password: '', displayName: '', role: 'ExecutiveAssistant' });
      loadUsers();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      setAddError(errors ? (Array.isArray(errors) ? errors.join(', ') : String(errors)) : 'Failed to create user');
    } finally {
      setAddLoading(false);
    }
  };

  if (!isAdmin) return null;
  if (loading) return null;
  if (error && users.length === 0) return null;

  return (
    <Card>
      <CardContent className="pt-6">
        <SectionHeader
          icon={UserPlus}
          title="User Management"
          right={
            <Button size="sm" onClick={() => setShowAdd(true)} className="gap-1.5">
              <UserPlus className="h-4 w-4" />
              Register User
            </Button>
          }
        />

        {error && <MessageAlert message={{ type: 'error', text: error }} onClose={() => setError(null)} />}

        <div className="flex flex-col gap-2">
          {users.map(u => (
            <div key={u.id} className="flex items-center justify-between rounded-md bg-muted p-3">
              <div>
                <p className="text-sm font-semibold">
                  {u.displayName}
                  {u.id === user?.id && (
                    <Badge variant="outline" className="ml-2 border-primary text-primary">You</Badge>
                  )}
                </p>
                <p className="text-sm text-muted-foreground">{u.email}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className="border-primary text-primary">{u.role}</Badge>
                {u.isActive
                  ? <Badge variant="outline" className="border-emerald-500 text-emerald-600">Active</Badge>
                  : <Badge variant="danger">Inactive</Badge>
                }
                {u.mfaEnabled && <Badge variant="success">MFA</Badge>}
                {u.id !== user?.id && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => handleToggleActive(u.id)}>
                          {u.isActive ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{u.isActive ? 'Deactivate user' : 'Reactivate user'}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          ))}
        </div>

        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Register New User</DialogTitle>
            </DialogHeader>
            {addError && <Alert variant="error"><AlertDescription>{addError}</AlertDescription></Alert>}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="reg-name">Display Name</Label>
                <Input id="reg-name" value={newUser.displayName} onChange={e => setNewUser(prev => ({ ...prev, displayName: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="reg-email">Email</Label>
                <Input id="reg-email" type="email" value={newUser.email} onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="reg-pw">Password</Label>
                <div className="flex gap-2">
                  <Input id="reg-pw" value={newUser.password} onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))} />
                  <Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => setNewUser(prev => ({ ...prev, password: generatePassword() }))}>Generate</Button>
                </div>
                <p className="text-xs text-muted-foreground">Min 8 chars, upper, lower, digit, special</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Executive">Executive</SelectItem>
                    <SelectItem value="ExecutiveAssistant">Executive Assistant</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={handleAddUser} disabled={addLoading}>
                {addLoading ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

const COMMON_TIMEZONES = [
  'Pacific/Auckland',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'UTC',
];

// ---------------------------------------------------------------------------
// Integrations (Slack / Teams)
// ---------------------------------------------------------------------------

function IntegrationsSection() {
  const [integrations, setIntegrations] = useState<import('../types/models').TenantIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newProvider, setNewProvider] = useState<'Slack' | 'Teams'>('Slack');
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newNotifyPlanProposed, setNewNotifyPlanProposed] = useState(true);
  const [newNotifyOverdueItems, setNewNotifyOverdueItems] = useState(true);
  const [newNotifyDelegations, setNewNotifyDelegations] = useState(true);
  const [newNotifyKpiAlerts, setNewNotifyKpiAlerts] = useState(true);

  const loadIntegrations = useCallback(async () => {
    try {
      const { integrationsApi } = await import('../api/services');
      const data = await integrationsApi.getAll();
      setIntegrations(data);
    } catch {
      // Admin-only endpoint — may 403 for non-admins
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadIntegrations(); }, [loadIntegrations]);

  const handleAdd = async () => {
    if (!newWebhookUrl.trim()) return;
    setSaving(true);
    setMessage(null);
    try {
      const { integrationsApi } = await import('../api/services');
      await integrationsApi.create({
        provider: newProvider,
        webhookUrl: newWebhookUrl,
        notifyPlanProposed: newNotifyPlanProposed,
        notifyOverdueItems: newNotifyOverdueItems,
        notifyDelegations: newNotifyDelegations,
        notifyKpiAlerts: newNotifyKpiAlerts,
      });
      setMessage({ type: 'success', text: `${newProvider} integration added.` });
      setShowAdd(false);
      setNewWebhookUrl('');
      await loadIntegrations();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create integration.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    try {
      const { integrationsApi } = await import('../api/services');
      await integrationsApi.update(id, { isEnabled: enabled } as any);
      await loadIntegrations();
    } catch {
      setMessage({ type: 'error', text: 'Failed to update integration.' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { integrationsApi } = await import('../api/services');
      await integrationsApi.delete(id);
      setMessage({ type: 'success', text: 'Integration removed.' });
      await loadIntegrations();
    } catch {
      setMessage({ type: 'error', text: 'Failed to delete integration.' });
    }
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    setMessage(null);
    try {
      const { integrationsApi } = await import('../api/services');
      await integrationsApi.test(id);
      setMessage({ type: 'success', text: 'Test message sent successfully.' });
    } catch {
      setMessage({ type: 'error', text: 'Test failed. Check your webhook URL.' });
    } finally {
      setTesting(null);
    }
  };

  const handleUpdateToggles = async (id: string, field: string, value: boolean) => {
    try {
      const { integrationsApi } = await import('../api/services');
      await integrationsApi.update(id, { [field]: value } as any);
      await loadIntegrations();
    } catch {
      setMessage({ type: 'error', text: 'Failed to update notification settings.' });
    }
  };

  if (loading) return null;

  return (
    <Card>
      <CardContent className="pt-6">
        <SectionHeader icon={Bell} title="Integrations (Slack / Teams)" />

        <p className="mb-3 text-sm text-muted-foreground">
          Send Cadence notifications to Slack or Microsoft Teams via incoming webhooks.
        </p>

        <MessageAlert message={message} onClose={() => setMessage(null)} />

        {integrations.map(integration => (
          <div key={integration.id} className="mb-3 rounded-md border border-border p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant={integration.provider === 'Slack' ? 'secondary' : 'default'}>{integration.provider}</Badge>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={integration.isEnabled} onCheckedChange={(v) => handleToggle(integration.id, v)} />
                  <span>{integration.isEnabled ? 'Enabled' : 'Disabled'}</span>
                </label>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => handleTest(integration.id)} disabled={testing === integration.id}>
                  {testing === integration.id ? 'Sending...' : 'Test'}
                </Button>
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(integration.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="mb-2 block font-mono text-xs text-muted-foreground">
              {integration.webhookUrl.substring(0, 50)}...
            </p>
            <div className="flex flex-wrap gap-3">
              <CheckboxRow
                checked={integration.notifyPlanProposed}
                onChange={(v) => handleUpdateToggles(integration.id, 'notifyPlanProposed', v)}
                label={<span className="text-xs">Plan proposed</span>}
              />
              <CheckboxRow
                checked={integration.notifyOverdueItems}
                onChange={(v) => handleUpdateToggles(integration.id, 'notifyOverdueItems', v)}
                label={<span className="text-xs">Overdue items</span>}
              />
              <CheckboxRow
                checked={integration.notifyDelegations}
                onChange={(v) => handleUpdateToggles(integration.id, 'notifyDelegations', v)}
                label={<span className="text-xs">Delegations</span>}
              />
              <CheckboxRow
                checked={integration.notifyKpiAlerts}
                onChange={(v) => handleUpdateToggles(integration.id, 'notifyKpiAlerts', v)}
                label={<span className="text-xs">KPI alerts</span>}
              />
            </div>
          </div>
        ))}

        {showAdd ? (
          <div className="mb-3 rounded-md border-2 border-primary p-3">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Provider</Label>
                <Select value={newProvider} onValueChange={(v) => setNewProvider(v as 'Slack' | 'Teams')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Slack">Slack</SelectItem>
                    <SelectItem value="Teams">Microsoft Teams</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="wh-url">Webhook URL</Label>
                <Input id="wh-url" placeholder="https://hooks.slack.com/services/..." value={newWebhookUrl} onChange={(e) => setNewWebhookUrl(e.target.value)} type="url" />
              </div>
              <div className="flex flex-wrap gap-3">
                <CheckboxRow checked={newNotifyPlanProposed} onChange={setNewNotifyPlanProposed} label={<span className="text-xs">Plan proposed</span>} />
                <CheckboxRow checked={newNotifyOverdueItems} onChange={setNewNotifyOverdueItems} label={<span className="text-xs">Overdue items</span>} />
                <CheckboxRow checked={newNotifyDelegations} onChange={setNewNotifyDelegations} label={<span className="text-xs">Delegations</span>} />
                <CheckboxRow checked={newNotifyKpiAlerts} onChange={setNewNotifyKpiAlerts} label={<span className="text-xs">KPI alerts</span>} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAdd} disabled={saving || !newWebhookUrl.trim()}>
                  {saving ? 'Adding...' : 'Add Integration'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setShowAdd(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Add Integration
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// External Task Sync
// ---------------------------------------------------------------------------

interface ExternalSyncConfig {
  id: string;
  provider: string;
  apiUrl: string | null;
  projectKey: string | null;
  filterQuery: string | null;
  isEnabled: boolean;
  autoImport: boolean;
  lastSyncedAt: string | null;
  createdAt: string;
}

function ExternalSyncSection() {
  const [configs, setConfigs] = useState<ExternalSyncConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; message: string } | null>(null);
  const [newConfig, setNewConfig] = useState({
    provider: 'Jira',
    apiUrl: '',
    apiToken: '',
    projectKey: '',
    filterQuery: '',
    autoImport: false,
  });

  const loadConfigs = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'https://localhost:7001/api'}/external-sync`,
        { headers: { Authorization: `Bearer ${JSON.parse(sessionStorage.getItem('auth') || '{}').accessToken}` } },
      );
      setConfigs(response.data);
      setError(null);
    } catch {
      setError('Failed to load sync configurations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const apiHeaders = () => ({
    Authorization: `Bearer ${JSON.parse(sessionStorage.getItem('auth') || '{}').accessToken}`,
  });
  const apiBase = `${process.env.REACT_APP_API_URL || 'https://localhost:7001/api'}/external-sync`;

  const handleCreate = async () => {
    if (!newConfig.provider) return;
    setFormLoading(true);
    setError(null);
    try {
      await axios.post(apiBase, { ...newConfig, isEnabled: true }, { headers: apiHeaders() });
      setShowAdd(false);
      setNewConfig({ provider: 'Jira', apiUrl: '', apiToken: '', projectKey: '', filterQuery: '', autoImport: false });
      setSuccess('Sync configuration created. Test the connection, then sync.');
      await loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create sync configuration.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this sync configuration?')) return;
    try {
      await axios.delete(`${apiBase}/${id}`, { headers: apiHeaders() });
      setSuccess('Sync configuration deleted.');
      await loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete.');
    }
  };

  const handleTest = async (id: string) => {
    setTestResult(null);
    try {
      const { data } = await axios.post(`${apiBase}/${id}/test`, {}, { headers: apiHeaders() });
      setTestResult({ id, success: data.success, message: data.success ? data.message : data.error });
    } catch (err: any) {
      setTestResult({ id, success: false, message: err.response?.data?.error || 'Test failed.' });
    }
  };

  const handleSync = async (id: string) => {
    setSyncingId(id);
    setError(null);
    try {
      const { data } = await axios.post(`${apiBase}/${id}/sync`, {}, { headers: apiHeaders() });
      if (data.success) {
        setSuccess(data.message);
      } else {
        setError(data.message || 'Sync failed.');
      }
      await loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Sync failed.');
    } finally {
      setSyncingId(null);
    }
  };

  const handleToggle = async (id: string, isEnabled: boolean) => {
    try {
      await axios.put(`${apiBase}/${id}`, { isEnabled }, { headers: apiHeaders() });
      await loadConfigs();
    } catch {
      setError('Failed to update.');
    }
  };

  const getApiUrlHint = (provider: string) => {
    switch (provider) {
      case 'Jira': return 'https://company.atlassian.net';
      case 'Linear': return 'Not needed for Linear';
      case 'Asana': return 'Not needed for Asana';
      default: return '';
    }
  };

  const getTokenHint = (provider: string) => {
    switch (provider) {
      case 'Jira': return 'Enter your Atlassian URL (e.g. company.atlassian.net), email, and API token from id.atlassian.com';
      case 'Linear': return 'Create a Personal API Key from linear.app/settings/api';
      case 'Asana': return 'Create a Personal Access Token from app.asana.com/0/developer-console';
      default: return 'API token or key';
    }
  };

  const getProjectKeyLabel = (provider: string) => {
    switch (provider) {
      case 'Jira': return 'Project Key';
      case 'Linear': return 'Team Key';
      case 'Asana': return 'Project GID';
      default: return 'Project Key';
    }
  };

  const getProjectKeyPlaceholder = (provider: string) => {
    switch (provider) {
      case 'Jira': return 'PROJ';
      case 'Linear': return 'ENG';
      case 'Asana': return '1234567890';
      default: return '';
    }
  };

  if (loading) return null;

  return (
    <Card>
      <CardContent className="pt-6">
        <SectionHeader
          icon={RefreshCw}
          title="External Task Sync"
          right={
            <Button variant="outline" size="sm" onClick={() => setShowAdd(true)} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add Sync
            </Button>
          }
        />

        <p className="mb-3 text-sm text-muted-foreground">
          Import tasks from Jira, Linear, or Asana into Cadence work items.
        </p>

        {error && <MessageAlert message={{ type: 'error', text: error }} onClose={() => setError(null)} />}
        {success && <MessageAlert message={{ type: 'success', text: success }} onClose={() => setSuccess(null)} />}

        {configs.length === 0 && !error && (
          <p className="py-2 text-center text-sm text-muted-foreground">
            No sync configurations. Add one to import tasks from external tools.
          </p>
        )}

        {configs.map(c => (
          <div key={c.id} className="mb-3 rounded-md border border-border p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="border-primary text-primary">{c.provider}</Badge>
                {c.projectKey && <Badge variant="outline">{c.projectKey}</Badge>}
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={c.isEnabled} onCheckedChange={(v) => handleToggle(c.id, v)} />
                  <span>{c.isEnabled ? 'Enabled' : 'Disabled'}</span>
                </label>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => handleTest(c.id)}>Test</Button>
                <Button size="sm" onClick={() => handleSync(c.id)} disabled={syncingId === c.id || !c.isEnabled}>
                  {syncingId === c.id ? 'Syncing...' : 'Sync Now'}
                </Button>
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(c.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {c.apiUrl && (
              <p className="block font-mono text-xs text-muted-foreground">{c.apiUrl}</p>
            )}
            {c.filterQuery && (
              <p className="block font-mono text-xs text-muted-foreground">Filter: {c.filterQuery}</p>
            )}
            {c.lastSyncedAt && (
              <p className="block text-xs text-muted-foreground">
                Last synced: {new Date(c.lastSyncedAt).toLocaleString()}
              </p>
            )}
            {c.autoImport && <Badge variant="outline" className="mt-1 border-blue-500 text-blue-600">Auto-import</Badge>}
            {testResult?.id === c.id && (
              <Alert variant={testResult.success ? 'success' : 'error'} className="mt-2">
                <AlertDescription className="text-xs">{testResult.message}</AlertDescription>
              </Alert>
            )}
          </div>
        ))}

        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add External Task Sync</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Provider</Label>
                <Select value={newConfig.provider} onValueChange={(v) => setNewConfig(prev => ({ ...prev, provider: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Jira">Jira</SelectItem>
                    <SelectItem value="Linear">Linear</SelectItem>
                    <SelectItem value="Asana">Asana</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newConfig.provider === 'Jira' && (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="api-url">API URL</Label>
                  <Input id="api-url" value={newConfig.apiUrl} onChange={e => setNewConfig(prev => ({ ...prev, apiUrl: e.target.value }))} placeholder={getApiUrlHint(newConfig.provider)} />
                  <p className="text-xs text-muted-foreground">{getApiUrlHint(newConfig.provider)}</p>
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="api-token">API Token</Label>
                <Input id="api-token" type="password" value={newConfig.apiToken} onChange={e => setNewConfig(prev => ({ ...prev, apiToken: e.target.value }))} />
                <p className="text-xs text-muted-foreground">{getTokenHint(newConfig.provider)}</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="proj-key">{getProjectKeyLabel(newConfig.provider)}</Label>
                <Input id="proj-key" value={newConfig.projectKey} onChange={e => setNewConfig(prev => ({ ...prev, projectKey: e.target.value }))} placeholder={getProjectKeyPlaceholder(newConfig.provider)} />
                <p className="text-xs text-muted-foreground">
                  {newConfig.provider === 'Jira' ? 'e.g. PROJ, ENG' : newConfig.provider === 'Linear' ? 'e.g. ENG' : 'e.g. 1234567890'}
                </p>
              </div>
              {newConfig.provider === 'Jira' && (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="jql-filter">JQL Filter</Label>
                  <Textarea id="jql-filter" rows={2} value={newConfig.filterQuery} onChange={e => setNewConfig(prev => ({ ...prev, filterQuery: e.target.value }))} placeholder="project = PROJ AND status != Done" />
                  <p className="text-xs text-muted-foreground">JQL query, e.g. assignee = currentUser() AND status != Done</p>
                </div>
              )}
              <SwitchRow
                checked={newConfig.autoImport}
                onChange={(v) => setNewConfig(prev => ({ ...prev, autoImport: v }))}
                label="Auto-import"
                description="Automatically create work items when syncing"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={formLoading}>
                {formLoading ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Theme / Font option tables
// ---------------------------------------------------------------------------

const THEME_OPTIONS = [
  { value: 'dark', label: 'Dark', description: 'Dark background, light text' },
  { value: 'light', label: 'Light', description: 'Light background, dark text' },
  { value: 'system', label: 'System', description: 'Match your operating system setting' },
] as const;

const FONT_OPTIONS = [
  { value: 'default', label: 'Default (Inter)', description: 'Clean, modern typeface' },
  { value: 'dyslexia', label: 'Dyslexia friendly', description: 'Optimised spacing, font, and contrast for easier reading' },
  { value: 'dyslexia-hc', label: 'Dyslexia friendly (high contrast)', description: 'Same as above with stronger contrast' },
] as const;

const FONT_SIZE_OPTIONS = [
  { value: 'small', label: 'Small', description: '13px' },
  { value: 'medium', label: 'Medium', description: '15px' },
  { value: 'large', label: 'Large', description: '17px' },
  { value: 'xl', label: 'Extra Large', description: '19px' },
] as const;

function OptionCard({ selected, onClick, label, description, disabled }: {
  selected: boolean;
  onClick: () => void;
  label: string;
  description: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        'flex-1 rounded-md border-2 p-3 text-left transition-colors',
        selected ? 'border-primary' : 'border-border',
        disabled ? 'pointer-events-none opacity-40' : 'hover:border-primary/70',
      )}
    >
      <p className="text-sm font-semibold">{label}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Browser Notifications
// ---------------------------------------------------------------------------

function BrowserNotificationsSection() {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    'Notification' in window ? Notification.permission : 'unsupported'
  );

  const handleRequestPermission = async () => {
    if (!('Notification' in window)) return;
    const result = await Notification.requestPermission();
    setPermission(result);
  };

  const statusLabel = permission === 'granted' ? 'Enabled' : permission === 'denied' ? 'Blocked' : permission === 'default' ? 'Not yet asked' : 'Not supported';
  const statusVariant = permission === 'granted' ? 'success' : permission === 'denied' ? 'danger' : 'outline';

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-3 flex items-center gap-2">
          <Bell className="h-5 w-5" />
          <h3 className="text-base font-semibold">Browser Notifications</h3>
          <Badge variant={statusVariant as any}>{statusLabel}</Badge>
        </div>
        <p className="mb-3 text-sm text-muted-foreground">
          Get notified about plan changes, delegations, and coaching insights even when Cadence is not in focus.
          Browser notifications are delivered via your system notification centre.
        </p>
        {permission === 'unsupported' && (
          <Alert variant="warning" className="mb-3"><AlertDescription>Your browser does not support notifications.</AlertDescription></Alert>
        )}
        {permission === 'denied' && (
          <Alert className="mb-3"><AlertDescription>
            Notifications are blocked. To re-enable them, update notification permissions for this site in your browser settings.
          </AlertDescription></Alert>
        )}
        {permission === 'granted' && (
          <Alert variant="success" className="mb-3"><AlertDescription>
            Browser notifications are active. You will receive alerts when Cadence is running in the background.
          </AlertDescription></Alert>
        )}
        {permission === 'default' && (
          <Button onClick={handleRequestPermission} className="gap-1.5">
            <Bell className="h-4 w-4" />
            Enable Notifications
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Stepper helper for AI Assistant Integration
// ---------------------------------------------------------------------------

function HorizontalSteps({ steps }: { steps: string[] }) {
  return (
    <ol className="mb-4 flex items-center gap-2">
      {steps.map((step, i) => (
        <React.Fragment key={i}>
          <li className="flex flex-1 flex-col items-center gap-1 text-center">
            <span className="flex h-7 w-7 items-center justify-center rounded-full border border-border text-xs font-semibold text-muted-foreground">
              {i + 1}
            </span>
            <span className="text-xs text-muted-foreground">{step}</span>
          </li>
          {i < steps.length - 1 && <div className="h-px flex-1 bg-border" />}
        </React.Fragment>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Main Preferences Page
// ---------------------------------------------------------------------------

export default function PreferencesPage() {
  const [prefs, setPrefs] = useState<Partial<PlanningPreference>>({});
  const [, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [oauthSources, setOauthSources] = useState<CalendarSource[]>([]);

  const { preferences: themePrefs, setPreferences: setThemePrefs } = useThemePreferences();
  const { variant: dashboardVariant, setVariant: setDashboardVariant } = useDashboardVariant();
  const { variant: unifiedPlanVariant, setVariant: setUnifiedPlanVariant } = useUnifiedPlanVariant();

  const [timezone, setTimezone] = useState(() => localStorage.getItem('cadence_timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [notifDailyBriefing, setNotifDailyBriefing] = useState(() => localStorage.getItem('cadence_notif_daily') !== 'false');
  const [notifPlanProposed, setNotifPlanProposed] = useState(() => localStorage.getItem('cadence_notif_plan') !== 'false');
  const [notifCarryForward, setNotifCarryForward] = useState(() => localStorage.getItem('cadence_notif_carry') !== 'false');
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [aiApiKey, setAiApiKey] = useState(() => localStorage.getItem('cadence_ai_api_key') || '');
  const [aiProvider, setAiProvider] = useState<'system' | 'openai' | 'google' | 'anthropic'>('system');

  const [mcpTestKey, setMcpTestKey] = useState('');
  const [mcpTesting, setMcpTesting] = useState(false);
  const [mcpTestResult, setMcpTestResult] = useState<{ success: boolean; message: string; toolCount?: number } | null>(null);
  const [showApiKeyFallback, setShowApiKeyFallback] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await preferencesApi.get();
        setPrefs(data);
        const model = data.preferredAiModel || '';
        if (model.startsWith('gpt-') || model.startsWith('o1') || model.startsWith('o3') || model.startsWith('o4')) {
          setAiProvider('openai');
        } else if (model.startsWith('gemini-')) {
          setAiProvider('google');
        } else if (model.startsWith('claude-') && model !== 'claude-sonnet-4-5-20250514') {
          setAiProvider('anthropic');
        } else {
          setAiProvider('system');
        }
      } catch (err) {
        console.error('Failed to load preferences', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    calendarSourcesApi.getAll()
      .then(sources => setOauthSources(sources.filter(s => s.sourceType === 'Microsoft365' || s.sourceType === 'Google')))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    localStorage.setItem('cadence_timezone', timezone);
    localStorage.setItem('cadence_notif_daily', String(notifDailyBriefing));
    localStorage.setItem('cadence_notif_plan', String(notifPlanProposed));
    localStorage.setItem('cadence_notif_carry', String(notifCarryForward));
    if (aiApiKey) localStorage.setItem('cadence_ai_api_key', aiApiKey);
    try {
      const toTimeOnly = (v?: string) => v ? (v.length === 5 ? `${v}:00` : v) : undefined;
      const payload = {
        workDayStart: toTimeOnly(prefs.workDayStart),
        workDayEnd: toTimeOnly(prefs.workDayEnd),
        meetingBufferMinutes: prefs.meetingBufferMinutes,
        meetingPrepMinutes: prefs.meetingPrepMinutes,
        maxFocusBlockMinutes: prefs.maxFocusBlockMinutes,
        minBlockMinutes: prefs.minBlockMinutes,
        lunchStartMinute: prefs.lunchStartMinute,
        lunchDurationMinutes: prefs.lunchDurationMinutes,
        icsCalendarUrl: prefs.icsCalendarUrl || undefined,
        treatPrivateAsPriority: prefs.treatPrivateAsPriority,
        preferredAiModel: prefs.preferredAiModel || undefined,
        minDailyFocusMinutes: prefs.minDailyFocusMinutes,
        idealDailyFocusMinutes: prefs.idealDailyFocusMinutes,
        preferredFocusTimeOfDay: prefs.preferredFocusTimeOfDay,
        minFocusBlockMinutes: prefs.minFocusBlockMinutes,
        autoAllocateFocusTime: prefs.autoAllocateFocusTime,
        writeFocusToCalendar: prefs.writeFocusToCalendar,
        focusCalendarSourceId: prefs.focusCalendarSourceId || undefined,
        briefingEmailEnabled: prefs.briefingEmailEnabled,
        briefingEmailDays: prefs.briefingEmailDays,
        briefingEmailTime: prefs.briefingEmailTime,
        briefingEmailTimezone: prefs.briefingEmailTimezone,
        bankHolidayRegion: prefs.bankHolidayRegion,
      };
      await preferencesApi.update(payload);
      setMessage({ type: 'success', text: 'Preferences saved' });
    } catch (err: any) {
      const data = err.response?.data;
      let msg = 'Failed to save';
      if (typeof data === 'string') msg = data;
      else if (data?.title) msg = data.title;
      else if (data?.errors) {
        const errs = Object.values(data.errors).flat();
        msg = errs.join(', ');
      } else if (data?.error) msg = data.error;
      else if (err.message) msg = err.message;
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleMcpTest = async () => {
    if (!mcpTestKey.trim()) return;
    setMcpTesting(true);
    setMcpTestResult(null);
    const apiUrl = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';
    try {
      const response = await axios.get(`${apiUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${mcpTestKey.trim()}` },
        timeout: 10000,
      });
      if (response.status === 200 && response.data?.id) {
        setMcpTestResult({
          success: true,
          message: `Connected! Cadence is now available in Claude with 40+ tools.`,
          toolCount: 40,
        });
      } else {
        setMcpTestResult({ success: false, message: 'Unexpected response. Check your API key and try again.' });
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setMcpTestResult({ success: false, message: 'API key is invalid or expired. Generate a new key above and try again.' });
      } else if (err.response?.status === 403) {
        setMcpTestResult({ success: false, message: 'API key does not have sufficient permissions. Check the key scopes.' });
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setMcpTestResult({ success: false, message: 'Connection timed out. Check that the server URL is correct and reachable.' });
      } else if (!err.response) {
        setMcpTestResult({ success: false, message: 'Network error. Check the server URL is correct and that your network allows the connection.' });
      } else {
        setMcpTestResult({ success: false, message: `Connection failed (${err.response?.status || 'unknown'}). Check your configuration and try again.` });
      }
    } finally {
      setMcpTesting(false);
    }
  };

  const briefingDays = (prefs.briefingEmailDays ?? '').split(',').map(s => s.trim()).filter(Boolean);
  const timezoneOptions = (Intl as any).supportedValuesOf?.('timeZone') ?? ['Europe/London', 'America/New_York', 'America/Los_Angeles', 'Asia/Singapore', 'Australia/Sydney', 'UTC'];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Settings</h2>
          <p className="text-sm text-muted-foreground">Manage your account, security, and preferences</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-1.5">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>

      <MessageAlert message={message} onClose={() => setMessage(null)} />

      <div className="flex flex-col gap-4">
        <AccountInfoSection />
        <MfaSetupSection />
        <PasskeysSection />
        <SessionManagementSection />
        <UserManagementSection />

        <div className="my-1 border-t border-border" />

        {/* Working Hours */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-3 text-base font-semibold">Working Hours</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="day-start">Day Start</Label>
                <Input id="day-start" type="time" value={prefs.workDayStart || '08:00'}
                  onChange={e => setPrefs(prev => ({ ...prev, workDayStart: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="day-end">Day End</Label>
                <Input id="day-end" type="time" value={prefs.workDayEnd || '18:00'}
                  onChange={e => setPrefs(prev => ({ ...prev, workDayEnd: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lunch-start">Lunch Start</Label>
                <Input
                  id="lunch-start"
                  type="time"
                  value={(() => {
                    const mins = prefs.lunchStartMinute ?? 720;
                    const h = Math.floor(mins / 60).toString().padStart(2, '0');
                    const m = (mins % 60).toString().padStart(2, '0');
                    return `${h}:${m}`;
                  })()}
                  onChange={e => {
                    const [h, m] = e.target.value.split(':').map(Number);
                    if (!isNaN(h) && !isNaN(m)) {
                      setPrefs(prev => ({ ...prev, lunchStartMinute: h * 60 + m }));
                    }
                  }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lunch-dur">Lunch Duration (min)</Label>
                <Input id="lunch-dur" type="number" value={prefs.lunchDurationMinutes || 60}
                  onChange={e => setPrefs(prev => ({ ...prev, lunchDurationMinutes: parseInt(e.target.value) }))} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scheduling */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-3 text-base font-semibold">Scheduling Rules</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="meet-buffer">Meeting Buffer (min)</Label>
                <Input id="meet-buffer" type="number" value={prefs.meetingBufferMinutes ?? 10}
                  onChange={e => setPrefs(prev => ({ ...prev, meetingBufferMinutes: parseInt(e.target.value) }))} />
                <p className="text-xs text-muted-foreground">Buffer after each meeting</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="meet-prep">Meeting Prep (min)</Label>
                <Input id="meet-prep" type="number" value={prefs.meetingPrepMinutes ?? 5}
                  onChange={e => setPrefs(prev => ({ ...prev, meetingPrepMinutes: parseInt(e.target.value) }))} />
                <p className="text-xs text-muted-foreground">Prep time before meetings</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="max-focus">Max Focus Block (min)</Label>
                <Input id="max-focus" type="number" value={prefs.maxFocusBlockMinutes ?? 90}
                  onChange={e => setPrefs(prev => ({ ...prev, maxFocusBlockMinutes: parseInt(e.target.value) }))} />
                <p className="text-xs text-muted-foreground">Longest uninterrupted work block</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="min-block">Min Block (min)</Label>
                <Input id="min-block" type="number" value={prefs.minBlockMinutes ?? 15}
                  onChange={e => setPrefs(prev => ({ ...prev, minBlockMinutes: parseInt(e.target.value) }))} />
                <p className="text-xs text-muted-foreground">Shortest schedulable block</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Focus Time Allocation */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-2 text-base font-semibold">Focus Time Allocation</h3>
            <p className="mb-3 text-sm text-muted-foreground">
              Controls how Start My Day reserves focus time each day. When enabled, the best gaps in your calendar are scored on length, meeting buffers, and time-of-day preference, and the top-scoring ones are claimed as focus blocks.
            </p>
            <SwitchRow
              checked={prefs.autoAllocateFocusTime ?? true}
              onChange={(v) => setPrefs(prev => ({ ...prev, autoAllocateFocusTime: v }))}
              label="Automatically allocate focus time when starting my day"
            />
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="min-daily">Minimum daily focus (min)</Label>
                <Input id="min-daily" type="number" value={prefs.minDailyFocusMinutes ?? 60}
                  onChange={e => setPrefs(prev => ({ ...prev, minDailyFocusMinutes: parseInt(e.target.value) }))} />
                <p className="text-xs text-muted-foreground">Floor: Start My Day warns below this.</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ideal-daily">Ideal daily focus (min)</Label>
                <Input id="ideal-daily" type="number" value={prefs.idealDailyFocusMinutes ?? 120}
                  onChange={e => setPrefs(prev => ({ ...prev, idealDailyFocusMinutes: parseInt(e.target.value) }))} />
                <p className="text-xs text-muted-foreground">Target: Start My Day aims for this.</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="min-block-size">Minimum block size (min)</Label>
                <Input id="min-block-size" type="number" value={prefs.minFocusBlockMinutes ?? 30}
                  onChange={e => setPrefs(prev => ({ ...prev, minFocusBlockMinutes: parseInt(e.target.value) }))} />
                <p className="text-xs text-muted-foreground">Gaps shorter than this are skipped</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Preferred time of day</Label>
                <Select
                  value={prefs.preferredFocusTimeOfDay ?? 'Morning'}
                  onValueChange={(v) => setPrefs(prev => ({ ...prev, preferredFocusTimeOfDay: v as 'Morning' | 'Afternoon' | 'Any' }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Morning">Morning</SelectItem>
                    <SelectItem value="Afternoon">Afternoon</SelectItem>
                    <SelectItem value="Any">Any</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <SwitchRow
                checked={prefs.writeFocusToCalendar ?? true}
                onChange={(v) => setPrefs(prev => ({ ...prev, writeFocusToCalendar: v }))}
                label="Write allocated focus blocks to my external calendar"
              />
            </div>
            {prefs.writeFocusToCalendar && oauthSources.length > 0 && (
              <div className="mt-3 flex flex-col gap-1.5">
                <Label>Write focus events to</Label>
                <Select
                  value={prefs.focusCalendarSourceId ?? '__auto__'}
                  onValueChange={(v) => setPrefs(prev => ({ ...prev, focusCalendarSourceId: v === '__auto__' ? undefined : v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__auto__">Auto (first active calendar)</SelectItem>
                    {oauthSources.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name} ({s.sourceType})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily briefing email */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-2 text-base font-semibold">Daily Briefing Email</h3>
            <p className="mb-3 text-sm text-muted-foreground">
              Controls the daily briefing email. Choose which days to receive it, the time of day, and the bank-holiday region so the email skips public holidays and Start My Day doesn't try to allocate work on them.
            </p>

            <SwitchRow
              checked={prefs.briefingEmailEnabled ?? true}
              onChange={(v) => setPrefs(prev => ({ ...prev, briefingEmailEnabled: v }))}
              label="Send me the daily briefing email"
            />

            <p className="mt-3 mb-1.5 text-sm font-semibold">Days to send</p>
            <div className="mb-3 flex flex-wrap gap-3">
              {(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const).map(day => {
                const checked = briefingDays.includes(day);
                return (
                  <CheckboxRow
                    key={day}
                    checked={checked}
                    disabled={!prefs.briefingEmailEnabled}
                    onChange={(v) => {
                      const current = new Set(briefingDays);
                      if (v) current.add(day);
                      else current.delete(day);
                      const canonical = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday']
                        .filter(d => current.has(d));
                      setPrefs(prev => ({ ...prev, briefingEmailDays: canonical.join(',') }));
                    }}
                    label={<span className="text-sm">{day.slice(0, 3)}</span>}
                  />
                );
              })}
            </div>

            <div className="mb-1 flex flex-col gap-3 sm:flex-row">
              <div className="flex flex-col gap-1.5" style={{ minWidth: 140 }}>
                <Label htmlFor="send-time">Send time</Label>
                <Input
                  id="send-time"
                  type="time"
                  value={prefs.briefingEmailTime ?? '06:00'}
                  onChange={e => setPrefs(prev => ({ ...prev, briefingEmailTime: e.target.value }))}
                  disabled={!prefs.briefingEmailEnabled}
                />
                <p className="text-xs text-muted-foreground">Resolution is 5 minutes (poll window).</p>
              </div>
              <div className="flex flex-col gap-1.5" style={{ minWidth: 260 }}>
                <Label htmlFor="tz">Timezone</Label>
                <Input
                  id="tz"
                  list="timezone-list"
                  value={prefs.briefingEmailTimezone ?? 'Europe/London'}
                  onChange={(e) => setPrefs(prev => ({ ...prev, briefingEmailTimezone: e.target.value || 'Europe/London' }))}
                  disabled={!prefs.briefingEmailEnabled}
                />
                <datalist id="timezone-list">
                  {timezoneOptions.map((tz: string) => <option key={tz} value={tz} />)}
                </datalist>
                <p className="text-xs text-muted-foreground">Detected: {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
              </div>
              <div className="flex flex-col gap-1.5" style={{ minWidth: 220 }}>
                <Label>Bank holiday region</Label>
                <Select
                  value={prefs.bankHolidayRegion ?? 'england-and-wales'}
                  onValueChange={(v) => setPrefs(prev => ({ ...prev, bankHolidayRegion: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="england-and-wales">England and Wales</SelectItem>
                    <SelectItem value="scotland">Scotland</SelectItem>
                    <SelectItem value="northern-ireland">Northern Ireland</SelectItem>
                    <SelectItem value="ie">Ireland</SelectItem>
                    <SelectItem value="us">United States</SelectItem>
                    <SelectItem value="au">Australia</SelectItem>
                    <SelectItem value="ca">Canada</SelectItem>
                    <SelectItem value="fr">France</SelectItem>
                    <SelectItem value="de">Germany</SelectItem>
                    <SelectItem value="es">Spain</SelectItem>
                    <SelectItem value="nl">Netherlands</SelectItem>
                    <SelectItem value="none">Ignore bank holidays</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Bank holidays skip the briefing email and Start My Day reports the date as a holiday with no work allocated.
            </p>
          </CardContent>
        </Card>

        <OutOfOfficeCard />

        {/* Calendar */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-2 text-base font-semibold">Calendar Integration</h3>
            <p className="text-sm text-muted-foreground">
              Connect and manage your calendars from the <strong>Calendar</strong> page.
              Click the settings icon on the Calendar page to connect Microsoft 365, Google Calendar, or ICS feeds.
            </p>
            <div className="mt-3">
              <SwitchRow
                checked={prefs.treatPrivateAsPriority ?? true}
                onChange={(v) => setPrefs(prev => ({ ...prev, treatPrivateAsPriority: v }))}
                label="Treat private calendar events as priority meetings"
              />
            </div>
          </CardContent>
        </Card>

        {/* AI */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-3 text-base font-semibold">AI Configuration</h3>
            <div className="mb-3 flex flex-col gap-1.5">
              <Label>Provider</Label>
              <Select
                value={aiProvider}
                onValueChange={(v) => {
                  const provider = v as typeof aiProvider;
                  setAiProvider(provider);
                  if (provider === 'system') {
                    setPrefs(prev => ({ ...prev, preferredAiModel: undefined }));
                  } else if (provider === 'openai') {
                    setPrefs(prev => ({ ...prev, preferredAiModel: 'gpt-4o' }));
                  } else if (provider === 'google') {
                    setPrefs(prev => ({ ...prev, preferredAiModel: 'gemini-2.0-flash' }));
                  } else if (provider === 'anthropic') {
                    setPrefs(prev => ({ ...prev, preferredAiModel: 'claude-sonnet-4-5-20250514' }));
                  }
                }}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">System Default</SelectItem>
                  <SelectItem value="openai">ChatGPT (OpenAI)</SelectItem>
                  <SelectItem value="google">Gemini (Google)</SelectItem>
                  <SelectItem value="anthropic">Claude (Anthropic)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {aiProvider === 'system' && (
              <Alert className="mb-2"><AlertDescription>
                Uses Cadence's built-in AI (currently Anthropic Claude). No configuration needed.
              </AlertDescription></Alert>
            )}

            {aiProvider === 'openai' && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="openai-key">OpenAI API Key</Label>
                  <Input id="openai-key" type="password" value={aiApiKey} onChange={e => setAiApiKey(e.target.value)} placeholder="sk-..." />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Model</Label>
                  <Select value={prefs.preferredAiModel || 'gpt-4o'} onValueChange={(v) => setPrefs(prev => ({ ...prev, preferredAiModel: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gpt-4o">GPT-4o (recommended)</SelectItem>
                      <SelectItem value="gpt-4o-mini">GPT-4o Mini (faster, lower cost)</SelectItem>
                      <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {aiProvider === 'google' && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="google-key">Google AI API Key</Label>
                  <Input id="google-key" type="password" value={aiApiKey} onChange={e => setAiApiKey(e.target.value)} placeholder="AIza..." />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Model</Label>
                  <Select value={prefs.preferredAiModel || 'gemini-2.0-flash'} onValueChange={(v) => setPrefs(prev => ({ ...prev, preferredAiModel: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gemini-2.0-flash">Gemini 2.0 Flash (recommended)</SelectItem>
                      <SelectItem value="gemini-1.5-pro">Gemini 1.5 Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {aiProvider === 'anthropic' && (
              <div className="flex flex-col gap-3">
                <Alert><AlertDescription>Use your own Anthropic API key instead of the system one.</AlertDescription></Alert>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="anth-key">Anthropic API Key</Label>
                  <Input id="anth-key" type="password" value={aiApiKey} onChange={e => setAiApiKey(e.target.value)} placeholder="sk-ant-..." />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Model</Label>
                  <Select value={prefs.preferredAiModel || 'claude-sonnet-4-5-20250514'} onValueChange={(v) => setPrefs(prev => ({ ...prev, preferredAiModel: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude-sonnet-4-5-20250514">Claude Sonnet 4.5 (recommended)</SelectItem>
                      <SelectItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (faster, lower cost)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {aiProvider !== 'system' && (
              <p className="mt-3 block text-xs text-muted-foreground">
                Your API key is stored securely and only used for your account.
              </p>
            )}
          </CardContent>
        </Card>

        <div className="my-1 border-t border-border" />

        {/* Timezone */}
        <Card>
          <CardContent className="pt-6">
            <SectionHeader icon={Globe} title="Timezone" />
            <div className="flex flex-col gap-1.5">
              <Label>Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMMON_TIMEZONES.map(tz => (
                    <SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="mt-2 block text-xs text-muted-foreground">
              Display-only preference. All scheduling uses your system timezone. Detected: {Intl.DateTimeFormat().resolvedOptions().timeZone}
            </p>
          </CardContent>
        </Card>

        {/* Dashboard preview */}
        <Card>
          <CardContent className="pt-6">
            <div className="mb-1 flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <h3 className="text-base font-semibold">Dashboard</h3>
              <Badge variant="outline" className="text-[10px]">Preview</Badge>
            </div>
            <p className="mb-3 text-sm text-muted-foreground">
              The briefing dashboard leads with one next action, three priorities, and a risk strip.
              Classic keeps the current multi-panel layout. You can switch at any time.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              {[
                { value: 'classic', label: 'Classic', description: 'Multi-panel view with full detail' },
                { value: 'briefing', label: 'Briefing', description: 'Signal-led, one next action, three priorities' },
              ].map((opt) => (
                <OptionCard
                  key={opt.value}
                  selected={dashboardVariant === opt.value}
                  onClick={() => setDashboardVariant(opt.value as 'classic' | 'briefing')}
                  label={opt.label}
                  description={opt.description}
                />
              ))}
            </div>

            <div className="mt-4">
              <div className="mb-1 flex items-center gap-2">
                <p className="text-sm font-semibold">Unified plan screen</p>
                <Badge variant="outline" className="text-[10px]">Preview</Badge>
              </div>
              <p className="mb-2 text-sm text-muted-foreground">
                Adds a dedicated Plan item to the navigation. Renders today's plan differently depending on its status, so Approve, Activate, Replan, and Close each get a distinct view.
              </p>
              <SwitchRow
                checked={unifiedPlanVariant === 'on'}
                onChange={(v) => setUnifiedPlanVariant(v ? 'on' : 'off')}
                label={unifiedPlanVariant === 'on' ? 'On' : 'Off'}
              />
            </div>
          </CardContent>
        </Card>

        {/* Theme */}
        <Card>
          <CardContent className="pt-6">
            <SectionHeader icon={Palette} title="Theme" />
            {isDyslexiaTheme(themePrefs.fontFamily) && (
              <p className="mb-2 block text-xs text-muted-foreground">
                Theme mode is managed by your reading preference below.
              </p>
            )}
            <div className="flex gap-2">
              {THEME_OPTIONS.map(opt => (
                <OptionCard
                  key={opt.value}
                  selected={themePrefs.mode === opt.value}
                  onClick={() => setThemePrefs({ ...themePrefs, mode: opt.value })}
                  label={opt.label}
                  description={opt.description}
                  disabled={isDyslexiaTheme(themePrefs.fontFamily)}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Accessibility */}
        <Card>
          <CardContent className="pt-6">
            <SectionHeader icon={Info} title="Accessibility" />

            <SwitchRow
              checked={themePrefs.highContrast}
              disabled={isDyslexiaTheme(themePrefs.fontFamily)}
              onChange={(v) => setThemePrefs({ ...themePrefs, highContrast: v })}
              label="High Contrast Mode"
              description={isDyslexiaTheme(themePrefs.fontFamily)
                ? 'Use "Dyslexia friendly (high contrast)" above instead'
                : 'Increases contrast for better readability'}
            />

            <div className="my-3 border-t border-border" />

            <p className="mb-2 text-sm font-semibold">Reading Preference</p>
            <div className="mb-3 flex flex-col gap-2">
              {FONT_OPTIONS.map(opt => (
                <OptionCard
                  key={opt.value}
                  selected={themePrefs.fontFamily === opt.value}
                  onClick={() => setThemePrefs({ ...themePrefs, fontFamily: opt.value as any })}
                  label={opt.label}
                  description={opt.description}
                />
              ))}
            </div>

            <div className="my-3 border-t border-border" />

            <p className="mb-2 text-sm font-semibold">Font Size</p>
            {isDyslexiaTheme(themePrefs.fontFamily) && (
              <p className="mb-2 block text-xs text-muted-foreground">
                Font size is optimised by your reading preference.
              </p>
            )}
            <div className="mb-3 flex gap-2">
              {FONT_SIZE_OPTIONS.map(opt => (
                <OptionCard
                  key={opt.value}
                  selected={themePrefs.fontSize === opt.value}
                  onClick={() => setThemePrefs({ ...themePrefs, fontSize: opt.value })}
                  label={opt.label}
                  description={opt.description}
                  disabled={isDyslexiaTheme(themePrefs.fontFamily)}
                />
              ))}
            </div>

            <div className="my-3 border-t border-border" />

            <SwitchRow
              checked={themePrefs.reducedMotion}
              onChange={(v) => setThemePrefs({ ...themePrefs, reducedMotion: v })}
              label="Reduce Motion"
              description="Minimise animations and transitions"
            />
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card>
          <CardContent className="pt-6">
            <SectionHeader icon={Bell} title="Notification Preferences" />
            <p className="mb-3 text-sm text-muted-foreground">Choose which email notifications you would like to receive.</p>
            <div className="flex flex-col gap-2">
              <CheckboxRow
                checked={notifDailyBriefing}
                onChange={setNotifDailyBriefing}
                label="Email daily briefing"
                description="Receive a summary of your planned day each morning"
              />
              <CheckboxRow
                checked={notifPlanProposed}
                onChange={setNotifPlanProposed}
                label="Email when plan proposed by EA"
                description="Get notified when your EA submits a daily plan for review"
              />
              <CheckboxRow
                checked={notifCarryForward}
                onChange={setNotifCarryForward}
                label="Email carry-forward reminders"
                description="Reminder when tasks are carried forward to the next day"
              />
            </div>
            <p className="mt-3 block text-xs text-muted-foreground">
              Notification preferences are saved and will take effect when email notifications are configured for your organisation.
            </p>
          </CardContent>
        </Card>

        <BrowserNotificationsSection />

        <div className="my-1 border-t border-border" />

        <h2 className="mt-1 text-xl font-semibold">Integrations</h2>

        <IntegrationsSection />
        <ExternalSyncSection />
        <ApiKeysSection />

        {/* MCP / AI Assistant Integration */}
        <Card>
          <CardContent className="pt-6">
            <SectionHeader icon={Sparkles} title="AI Assistant Integration" />
            <p className="mb-4 text-sm text-muted-foreground">
              Connect Cadence to Claude Desktop or another OAuth-compatible MCP client.
              The MCP server gives your AI assistant access to 40+ Cadence tools including daily planning, work items, calendar, and transcripts.
            </p>

            <HorizontalSteps steps={["Paste server URL", "Sign in & approve", "You're connected"]} />

            <div className="mb-3 rounded-md bg-muted p-3">
              <p className="mb-2 text-sm font-semibold">Step 1: Add the connector in Claude Desktop</p>
              <p className="mb-2 text-sm text-muted-foreground">
                1. Open Claude Desktop → <strong>Settings</strong> → <strong>Integrations</strong><br />
                2. Click <strong>Add custom connector</strong><br />
                3. Paste the URL below and leave both OAuth fields blank.
              </p>
              <div className="mb-2 pl-3">
                <p className="text-sm"><strong>Name:</strong> Cadence</p>
                <p className="break-all text-sm">
                  <strong>Remote MCP server URL:</strong> https://api.stratiumhq.com/mcp
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5"
                onClick={() => {
                  navigator.clipboard.writeText('https://api.stratiumhq.com/mcp');
                  setCopySuccess('mcp-desktop');
                  setTimeout(() => setCopySuccess(null), 2000);
                }}
              >
                <Copy className="h-4 w-4" />
                {copySuccess === 'mcp-desktop' ? 'Copied' : 'Copy Server URL'}
              </Button>
            </div>

            <div className="mb-3 rounded-md bg-muted p-3">
              <p className="mb-2 text-sm font-semibold">Step 2: Sign in and approve</p>
              <p className="text-sm text-muted-foreground">
                Claude Desktop opens a browser tab on Cadence. Sign in with your normal Cadence account
                (passkey, password, or magic link — whatever you usually use), then click <strong>Allow</strong>
                on the consent screen. The connector finishes setting itself up automatically; no API key,
                no client secret, no copy-paste of tokens.
              </p>
            </div>

            <div className="mb-3 rounded-md bg-muted p-3">
              <p className="mb-2 text-sm font-semibold">Step 3: You're connected</p>
              <p className="text-sm text-muted-foreground">
                Cadence appears as a connector with 40+ tools. The connection shows up under{' '}
                <a href="/connections" className="text-primary underline">Connections → Connected apps</a>; disconnect any time
                from there to revoke access immediately.
              </p>
            </div>

            <Alert className="mb-3"><AlertDescription>
              No software install, no API key handling. Cadence implements the standard OAuth 2.1 +
              Dynamic Client Registration flow that Claude Desktop's connector UI expects.
            </AlertDescription></Alert>

            <div className="my-3 border-t border-border" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowApiKeyFallback(s => !s)}
              className="normal-case"
            >
              {showApiKeyFallback ? 'Hide' : 'Show'} API key option (CLI / scripting / Claude Code)
            </Button>
            {showApiKeyFallback && (
              <div className="mt-3">
                <p className="mb-3 text-sm text-muted-foreground">
                  Tools that don't speak OAuth — terminal MCP clients, custom scripts, CI jobs —
                  can still authenticate with a long-lived API key. Generate one in{' '}
                  <strong>Your API Keys</strong> above, then use it as a Bearer token (preferred)
                  or as a <code>?key=</code> query parameter (legacy, still supported).
                </p>

                <p className="mb-1 text-xs font-semibold text-primary">Claude Code (terminal)</p>
                <pre className="overflow-auto whitespace-pre-wrap break-all rounded-md border border-border bg-background p-3 font-mono text-xs">
{`claude mcp add cadence \\
  --transport sse \\
  "https://api.stratiumhq.com/mcp?key=cad_k1_paste_your_key_here"`}
                </pre>
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-1 mb-3 gap-1.5"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `claude mcp add cadence --transport sse "https://api.stratiumhq.com/mcp?key=cad_k1_paste_your_key_here"`
                    );
                    setCopySuccess('mcp-code');
                    setTimeout(() => setCopySuccess(null), 2000);
                  }}
                >
                  <Copy className="h-4 w-4" />
                  {copySuccess === 'mcp-code' ? 'Copied' : 'Copy Command'}
                </Button>

                <div className="mb-2 rounded-md bg-muted p-3">
                  <p className="mb-2 text-sm font-semibold">Test an API key</p>
                  <p className="mb-2 text-sm text-muted-foreground">Paste a key to verify it can reach the MCP server.</p>
                  <div className="flex items-start gap-2">
                    <Input
                      value={mcpTestKey}
                      onChange={e => setMcpTestKey(e.target.value)}
                      placeholder="cad_k1_..."
                      type="password"
                      className="flex-1 font-mono text-xs"
                    />
                    <Button onClick={handleMcpTest} disabled={mcpTesting || !mcpTestKey.trim()} className="gap-1.5">
                      {mcpTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      {mcpTesting ? 'Testing…' : 'Test Connection'}
                    </Button>
                  </div>
                  {mcpTestResult && (
                    <Alert variant={mcpTestResult.success ? 'success' : 'error'} className="mt-2">
                      <AlertDescription>{mcpTestResult.message}</AlertDescription>
                    </Alert>
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  API keys are stored as SHA-256 hashes; the plaintext is never persisted server-side.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* API Access */}
        <Card>
          <CardContent className="pt-6">
            <SectionHeader icon={Globe} title="API Access" />
            <p className="mb-3 text-sm text-muted-foreground">
              Integrate Cadence directly into your own tools and workflows via the REST API.
              Generate an API key above to authenticate.
            </p>
            <div className="mb-3 flex items-center gap-2">
              <div className="flex-1">
                <Label htmlFor="api-base-url" className="text-xs">API Base URL</Label>
                <Input
                  id="api-base-url"
                  value="https://api.stratiumhq.com/api"
                  readOnly
                  className="font-mono"
                />
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mt-5"
                      onClick={() => {
                        navigator.clipboard.writeText('https://api.stratiumhq.com/api');
                        setCopySuccess('api');
                        setTimeout(() => setCopySuccess(null), 2000);
                      }}
                    >
                      {copySuccess === 'api' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy URL</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-xs text-muted-foreground">
              Use your API key in the Authorization header: <code>Authorization: Bearer cad_k1_...</code>.
              Web-app users authenticate with their session JWT; MCP clients use OAuth (see AI Assistant Integration above).
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
